import { Flex, Text, ThemeUIStyleObject } from 'theme-ui';
import { TextX } from '~/types/style';
import { textXtoMarginX } from '~/utils/style-helpers';
import LinkRenderer from '../LinkRenderer';
import useCTAStyle from '~/hooks/components/use-cta-style';

type Props = Queries.SectionHeaderFragmentFragment & {
  color: string;
  ctaButton?: Queries.CallToActionFragmentFragment;
  noMaxWidth?: boolean;
  titleSx?: ThemeUIStyleObject;
};
const Site3SectionHeader = (props: Props) => {
  const desktopWidth = '43.8rem';
  const mobileWidth = '34.2rem';
  const sharedStyle = {
    maxWidth: props.noMaxWidth ? 'var(--max-width)' : [mobileWidth, desktopWidth],
    '& > p': { margin: 0 },
  };
  const { ctaButton, ctaUnderTitle } = props;
  // Body style
  const textDirectionBody = props.textDirectionBody ?? 'center';
  const textDirectionMobileBody = props.textDirectionMobileBody ?? textDirectionBody;

  const textDirectionStyleBody = {
    ...sharedStyle,
    textAlign: [textDirectionMobileBody, textDirectionBody],
    mx: [textXtoMarginX(textDirectionMobileBody), textXtoMarginX(textDirectionBody)],
    maxWidth: ['100%', desktopWidth],
    mb: '0.8rem',
  };

  // Title style
  const textDirectionTitle = (props.textDirection?.toLowerCase() as TextX) ?? 'center';
  const textDirectionMobileTitle = (props.textDirectionMobile?.toLowerCase() as TextX) ?? textDirectionTitle;

  const textDirectionStyleTitle: ThemeUIStyleObject = {
    textAlign: [textDirectionMobileTitle, textDirectionTitle],
    mx: [textXtoMarginX(textDirectionMobileTitle), props.noMaxWidth ? 'auto' : textXtoMarginX(textDirectionTitle)],
    width: '100%',
  };

  const titleStyle: ThemeUIStyleObject = {
    fontSize: ['2.4rem', '3.2rem'],
    lineHeight: 1,
    letterSpacing: ['-0.02em', 0],
    fontWeight: 500,
    ...sharedStyle,
    ...textDirectionStyleTitle,
    ...props.titleSx,
  };

  const bodyText = props.description && props.description?.childMarkdownRemark?.html;

  const mobileTitle = props?.mobileTitle && props.mobileTitle.length > 0 ? props.mobileTitle : props.title ?? '';

  const TitleComponent = !!props.title && (
    <>
      <Text
        as="h3"
        variant="h6"
        sx={{
          display: ['none', 'block'],
          ...titleStyle,
        }}
        dangerouslySetInnerHTML={{ __html: props.title }}
      />
      <Text
        as="h3"
        variant="h6"
        sx={{
          display: ['block', 'none'],
          ...titleStyle,
        }}
        dangerouslySetInnerHTML={{ __html: mobileTitle }}
      />
    </>
  );

  const SubtitleComponent = !!props.subtitle && (
    // subtitle follows title style
    <Text
      as="h4"
      variant="copy"
      sx={{ ...textDirectionStyleTitle, lineHeight: 1.2, mb: '0.8rem' }}
      dangerouslySetInnerHTML={{ __html: props.subtitle }}
    />
  );

  const BodyComponent = !!bodyText && (
    <Text
      variant="copy"
      sx={{ ...textDirectionStyleBody, lineHeight: 1.2 }}
      dangerouslySetInnerHTML={{ __html: bodyText }}
    />
  );

  const CTAStyles = useCTAStyle({
    baseColor: ctaButton?.baseColor ?? 'black',
    variant: ctaButton?.variant ?? 'filled',
    size: ctaButton?.size ?? 'medium',
    disableDisabledStyle: false,
  });

  return (
    <Flex
      sx={{
        flexDirection: 'column',
        gap: '0.8rem',
        px: props.noMaxWidth ? null : ['3md', null, 'lg'], // Add padding to match the padding of the section
        color: props?.color ?? 'black',
      }}
    >
      {TitleComponent}
      {SubtitleComponent}
      {BodyComponent}
      {Boolean(ctaButton) && ctaUnderTitle && (
        // @ts-ignore
        <LinkRenderer
          sx={{ ...CTAStyles, alignSelf: 'center' }}
          slug={ctaButton?.page?.slug}
          innerLink={ctaButton?.innerLink}
        >
          {ctaButton?.text}
        </LinkRenderer>
      )}
    </Flex>
  );
};

export default Site3SectionHeader;
